import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { FormField } from '@angular/forms/signals';
import { RouterLink } from '@angular/router';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { CampoHoneypotComponent } from '../../../../shared/components/campo-honeypot/campo-honeypot.component';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { AlertaService } from '../../../../shared/utils/alerta.service';
import { AgendamentoEscolhido, UFS } from '../../models/solicitacao-servico.model';
import { PerfilCidadaoService } from '../../services/perfil-cidadao.service';
import { CampoSolicitacaoComponent } from '../campo-solicitacao/campo-solicitacao.component';
import { ModalAgendamentoComponent } from '../modal-agendamento/modal-agendamento.component';
import { LIMITE_DESCRICAO, SolicitacaoServicoStore } from '../solicitacao-servico.store';

/**
 * A etapa Solicitação: o endereço, a descrição, os campos específicos que o serviço declara e —
 * só quando o serviço é de agendamento — a data e o horário.
 *
 * Não tem formulário próprio: lê e escreve no `SolicitacaoServicoStore` do fluxo, e por isso o
 * que foi digitado sobrevive à troca de etapas.
 */
@Component({
  selector: 'app-etapa-solicitacao',
  templateUrl: './etapa-solicitacao.component.html',
  styleUrls: ['./etapa-solicitacao.component.css'],
  imports: [
    LucideModule,
    FormField,
    ModalAgendamentoComponent,
    RouterLink,
    NgxMaskDirective,
    CampoSolicitacaoComponent,
    CampoHoneypotComponent,
  ],
  providers: [provideNgxMask({ dropSpecialCharacters: false })],
})
export class EtapaSolicitacaoComponent {
  private readonly alerta = inject(AlertaService);
  private readonly perfil = inject(PerfilCidadaoService);
  protected readonly store = inject(SolicitacaoServicoStore);

  /** Só com login pelo sistema a etapa exibe o cadastro; sem ele os dados não aparecem. */
  readonly identificado = computed(() => this.store.modoAcesso() === 'identificado');

  /**
   * Dados do cadastro mostrados apenas para conferência: a alteração acontece na área do
   * cidadão, então esta etapa não os coloca no formulário nem permite editá-los.
   */
  readonly dadosPessoais = computed(() => {
    const cadastro = this.perfil.cadastro();

    return {
      nomeCompleto: cadastro.nomeCompleto,
      cpf: cadastro.cpf,
      telefone: cadastro.telefoneCelular01 || cadastro.telefoneResidencial,
      email: cadastro.email,
    };
  });

  readonly dadosPessoaisAberto = signal(true);

  readonly enderecoAberto = signal(true);

  readonly ufs = UFS;

  readonly limiteDescricao = LIMITE_DESCRICAO;

  readonly solicitacaoForm = this.store.solicitacaoForm;

  readonly mostrarPendencias = this.store.pendenciasVisiveis;

  readonly caracteresDescricao = computed(() => this.store.dados().descricao.length);

  readonly codServico = computed(() => this.store.formulario()?.codServicoSrv ?? null);

  readonly agendamentoFormatado = computed(() => {
    const escolha = this.store.agendamento();

    if (!escolha) return '';

    const [ano, mes, dia] = escolha.data.split('-');

    return `${dia}/${mes}/${ano}`;
  });

  readonly agendamentoPendente = computed(
    () => this.mostrarPendencias() && this.store.exigeAgendamento() && !this.store.agendamento(),
  );

  private readonly modalAgendamento = viewChild(ModalAgendamentoComponent);

  abrirAgendamento(): void {
    this.modalAgendamento()?.abrir(this.store.agendamento());
  }

  aoAgendar(escolha: AgendamentoEscolhido): void {
    this.store.agendamento.set(escolha);
  }

  /** A via decide o prazo que o órgão tem: a segunda via usa o prazo de reemissão do serviço. */
  escolherVia(segundaVia: boolean): void {
    this.store.segundaVia.set(segundaVia);
  }

  async removerAgendamento(): Promise<void> {
    const confirmado = await this.alerta.confirmar({
      titulo: 'Remover agendamento?',
      texto: `O atendimento marcado para ${this.agendamentoFormatado()} às ${this.store.agendamento()?.horario} será desmarcado. Você pode agendar outra data depois.`,
      confirmar: 'Sim, remover',
      cancelar: 'Manter agendamento',
    });

    if (confirmado) {
      this.store.agendamento.set(null);
    }
  }

  recarregarFormulario(): void {
    this.store.recarregarFormulario();
  }

  alternarDadosPessoais(): void {
    this.dadosPessoaisAberto.update((aberto) => !aberto);
  }

  alternarEndereco(): void {
    this.enderecoAberto.update((aberto) => !aberto);
  }
}
